/**
 * DeploymentPathSelector
 *
 * Shows deployment options for organic vs paid content after scheduling.
 * Organic content goes directly to the posts collection for publishing.
 * Paid content opens the DeployToFunnelModal for funnel configuration.
 */

import React from 'react';
import { Send, Target, Calendar, TrendingUp, Megaphone, ArrowRight } from 'lucide-react';

const DeploymentPathSelector = ({
    organicCount = 0,
    paidCount = 0,
    organicScheduled = false,
    onPaidDeploy,
    isLoading = false
}) => {
    const totalCount = organicCount + paidCount;
    const hasOrganic = organicCount > 0;
    const hasPaid = paidCount > 0;

    if (totalCount === 0) return null;

    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-4)',
            padding: 'var(--space-4)',
            background: 'var(--color-bg-secondary)',
            borderRadius: 'var(--radius-lg)',
            border: '1px solid var(--color-border)'
        }}>
            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-2)',
                marginBottom: 'var(--space-2)'
            }}>
                <Target size={20} style={{ color: 'var(--color-primary)' }} />
                <h4 style={{ margin: 0 }}>Deployment Summary</h4>
            </div>

            <div style={{
                display: 'grid',
                gridTemplateColumns: hasPaid && hasOrganic ? '1fr 1fr' : '1fr',
                gap: 'var(--space-4)'
            }}>
                {/* Organic Content Card */}
                {hasOrganic && (
                    <div style={{
                        padding: 'var(--space-4)',
                        background: organicScheduled ? 'var(--success-50)' : 'var(--color-bg-primary)',
                        borderRadius: 'var(--radius-md)',
                        border: organicScheduled
                            ? '2px solid var(--success-300)'
                            : '1px solid var(--color-border)'
                    }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--space-2)',
                            marginBottom: 'var(--space-3)'
                        }}>
                            <div style={{
                                width: 32,
                                height: 32,
                                borderRadius: 'var(--radius-md)',
                                background: organicScheduled ? 'var(--success-500)' : 'var(--color-primary)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                <Calendar size={16} style={{ color: 'white' }} />
                            </div>
                            <div>
                                <h5 style={{ margin: 0, fontSize: 'var(--font-size-md)' }}>
                                    Organic Content
                                </h5>
                                <span style={{
                                    fontSize: 'var(--font-size-sm)',
                                    color: 'var(--color-text-secondary)'
                                }}>
                                    {organicCount} post{organicCount !== 1 ? 's' : ''}
                                </span>
                            </div>
                        </div>

                        <div style={{
                            fontSize: 'var(--font-size-sm)',
                            color: 'var(--color-text-secondary)',
                            marginBottom: 'var(--space-3)'
                        }}>
                            {organicScheduled ? (
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 'var(--space-2)',
                                    color: 'var(--success-600)'
                                }}>
                                    <Send size={14} />
                                    Scheduled for auto-publishing
                                </div>
                            ) : (
                                'Will be scheduled for automatic posting to your connected social accounts'
                            )}
                        </div>

                        <div style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 'var(--space-2)'
                        }}>
                            <Badge icon={<Calendar size={12} />} text="Auto-publish" />
                            <Badge icon={<TrendingUp size={12} />} text="Optimal times" />
                        </div>
                    </div>
                )}

                {/* Paid Content Card */}
                {hasPaid && (
                    <div style={{
                        padding: 'var(--space-4)',
                        background: 'var(--color-bg-primary)',
                        borderRadius: 'var(--radius-md)',
                        border: '1px solid var(--color-border)'
                    }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--space-2)',
                            marginBottom: 'var(--space-3)'
                        }}>
                            <div style={{
                                width: 32,
                                height: 32,
                                borderRadius: 'var(--radius-md)',
                                background: 'var(--warning-500)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                <Megaphone size={16} style={{ color: 'white' }} />
                            </div>
                            <div>
                                <h5 style={{ margin: 0, fontSize: 'var(--font-size-md)' }}>
                                    Paid Campaigns
                                </h5>
                                <span style={{
                                    fontSize: 'var(--font-size-sm)',
                                    color: 'var(--color-text-secondary)'
                                }}>
                                    {paidCount} creative{paidCount !== 1 ? 's' : ''}
                                </span>
                            </div>
                        </div>

                        <div style={{
                            fontSize: 'var(--font-size-sm)',
                            color: 'var(--color-text-secondary)',
                            marginBottom: 'var(--space-3)'
                        }}>
                            Configure targeting, budgets, and placements before deploying to ad platforms
                        </div>

                        <div style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 'var(--space-2)',
                            marginBottom: 'var(--space-3)'
                        }}>
                            <Badge icon={<Target size={12} />} text="Audience targeting" />
                            <Badge icon={<TrendingUp size={12} />} text="Budget allocation" />
                        </div>

                        <button
                            onClick={onPaidDeploy}
                            disabled={isLoading}
                            className="btn btn-primary"
                            style={{ width: '100%' }}
                        >
                            <Megaphone size={16} />
                            Deploy to Ad Platforms
                            <ArrowRight size={16} />
                        </button>
                    </div>
                )}
            </div>

            {/* Summary footer */}
            {hasOrganic && hasPaid && (
                <div style={{
                    fontSize: 'var(--font-size-sm)',
                    color: 'var(--color-text-secondary)',
                    textAlign: 'center',
                    paddingTop: 'var(--space-2)',
                    borderTop: '1px solid var(--color-border)'
                }}>
                    Organic content is automatically scheduled. Paid content requires additional configuration.
                </div>
            )}
        </div>
    );
};

// Badge component for feature highlights
const Badge = ({ icon, text }) => (
    <div style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 'var(--space-1)',
        padding: 'var(--space-1) var(--space-2)',
        background: 'var(--color-bg-tertiary)',
        borderRadius: 'var(--radius-sm)',
        fontSize: 'var(--font-size-xs)',
        color: 'var(--color-text-secondary)'
    }}>
        {icon}
        {text}
    </div>
);

export default DeploymentPathSelector;
